// @generated-imports
import { showToast } from './app.js';
import { createBackupObject, renderBackupSummary } from './backup.js';
import { CONFIG, dom, state } from './core.js';
import { describeEmuDb } from './datapanel.js';
import { getSetting, markBackupDone } from './platform.js';
import { shareOrDownloadFile } from './states.js';
// @generated-imports
/* ===========================================================================
   BACKUP MAIS FÁCIL DE GUARDAR (v2.1)
   ---------------------------------------------------------------------------
   • Compartilhar: no celular, o arquivo vai direto para WhatsApp, Drive,
     e-mail (Web Share com arquivos); sem suporte, baixa como antes.
   • Pasta de backup: no Chrome/Edge de computador, escolha uma pasta uma vez
     (File System Access) e o EmuTab grava o backup nela — manualmente ou
     sozinho, todo dia/semana (⚙️). O identificador da pasta fica no banco
     `emutab-handles`, que NÃO entra no backup (não é transportável).
   ======================================================================== */

const HANDLES_DB = 'emutab-handles';

function backupFileName(backup) {
  const stamp = new Date().toISOString().slice(0, 10);
  const saveCount = (backup.databases || [])
    .reduce((sum, db) => sum + (db.stores || []).reduce((acc, store) => acc + (store.entries || []).length, 0), 0);
  return `emutab-backup-${stamp}-${saveCount}saves${backup.partial ? '-PARCIAL' : ''}.json`;
}

async function buildBackupFile() {
  const backup = await createBackupObject();
  const file = new File([JSON.stringify(backup)], backupFileName(backup), { type: 'application/json' });
  return { file, backup };
}

function reportBackupResult(backup, verb) {
  if (backup.partial) {
    showToast(`Backup PARCIAL ${verb} — ${backup.inventory.failed.length} banco(s) não puderam ser lidos (${backup.inventory.failed.map(describeEmuDb).map((d) => d.label).join(', ')}). Feche o jogo/outras abas e tente de novo.`);
  } else {
    markBackupDone();
    showToast(`Backup ${verb}. Guarde o arquivo em local seguro.`);
  }
  renderBackupSummary();
}

async function shareBackup() {
  try {
    const { file, backup } = await buildBackupFile();
    const result = await shareOrDownloadFile(file, 'Backup do EmuTab');
    if (result !== 'cancelled') reportBackupResult(backup, result === 'shared' ? 'compartilhado' : 'exportado');
  } catch (error) {
    showToast(`Não consegui gerar o backup: ${(error && error.message) || error}`);
  }
}

function canShareFiles() {
  try {
    return Boolean(navigator.canShare && navigator.share && navigator.canShare({ files: [new File(['x'], 'x.json', { type: 'application/json' })] }));
  } catch (_) { return false; }
}

/* --- Pasta de backup ----------------------------------------------------- */

function folderBackupSupported() {
  return typeof window.showDirectoryPicker === 'function';
}

function openHandlesDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(HANDLES_DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore('handles');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getBackupFolder() {
  try {
    const db = await openHandlesDb();
    try {
      return await new Promise((resolve) => {
        const req = db.transaction('handles', 'readonly').objectStore('handles').get('backup-folder');
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      });
    } finally { db.close(); }
  } catch (_) { return null; }
}

async function saveBackupFolder(handle) {
  const db = await openHandlesDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction('handles', 'readwrite');
      tx.objectStore('handles').put(handle, 'backup-folder');
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

async function chooseBackupFolder() {
  if (!folderBackupSupported()) { showToast('Este navegador não permite escolher uma pasta (use Chrome ou Edge no computador).'); return; }
  try {
    const handle = await window.showDirectoryPicker({ id: 'emutab-backup', mode: 'readwrite' });
    await saveBackupFolder(handle);
    const result = await writeBackupToFolder({ interactive: true });
    showToast(result === 'ok' ? `Pasta "${handle.name}" escolhida — backup gravado nela.` : result === 'partial' ? 'Pasta escolhida — backup gravado, mas PARCIAL.' : 'Pasta escolhida, mas sem permissão de escrita.');
    renderFolderBackupStatus();
  } catch (error) {
    if (!error || error.name !== 'AbortError') showToast('Não foi possível usar essa pasta.');
  }
}

/** Grava o backup na pasta. Retorna ok | partial | no-folder | needs-permission | denied | error. */
async function writeBackupToFolder(options = {}) {
  const handle = await getBackupFolder();
  if (!handle) return 'no-folder';
  try {
    let permission = typeof handle.queryPermission === 'function' ? await handle.queryPermission({ mode: 'readwrite' }) : 'granted';
    if (permission !== 'granted') {
      if (!options.interactive || typeof handle.requestPermission !== 'function') return 'needs-permission';
      permission = await handle.requestPermission({ mode: 'readwrite' });
      if (permission !== 'granted') return 'denied';
    }
    const { file, backup } = await buildBackupFile();
    const fileHandle = await handle.getFileHandle(file.name, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(file);
    await writable.close();
    try { localStorage.setItem(CONFIG.localStorageFolderBackupKey, String(Date.now())); } catch (_) {}
    if (!backup.partial) markBackupDone();
    renderFolderBackupStatus();
    return backup.partial ? 'partial' : 'ok';
  } catch (_) {
    return 'error';
  }
}

/** Boot e fim de sessão: grava sozinho quando o intervalo venceu. */
async function maybeAutoFolderBackup() {
  const days = Number(getSetting('folderBackupDays')) || 0;
  if (!days || !folderBackupSupported() || state.session) return null;
  const last = Number(localStorage.getItem(CONFIG.localStorageFolderBackupKey)) || 0;
  if (Date.now() - last < days * 86400000) return null;
  const result = await writeBackupToFolder({ interactive: false });
  if (result === 'ok') showToast('💾 Backup automático gravado na sua pasta.');
  else if (result === 'partial') showToast('💾 Backup automático gravado, mas PARCIAL — confira o painel 💾.');
  else if (result === 'needs-permission') {
    showToast('O backup automático precisa de autorização para gravar na pasta.', {
      action: { label: 'Autorizar', handler: () => writeBackupToFolder({ interactive: true }).then((r) => showToast(r === 'ok' ? 'Backup gravado na pasta.' : 'Não foi possível gravar na pasta.')) },
    });
  }
  return result;
}

async function renderFolderBackupStatus() {
  if (!dom.folderBackupStatus) return;
  if (!folderBackupSupported()) {
    dom.folderBackupStatus.textContent = 'Pasta de backup: disponível no Chrome/Edge de computador.';
    if (dom.backupFolderBtn) dom.backupFolderBtn.classList.add('is-hidden');
    return;
  }
  const handle = await getBackupFolder();
  const last = Number(localStorage.getItem(CONFIG.localStorageFolderBackupKey)) || 0;
  const days = Number(getSetting('folderBackupDays')) || 0;
  dom.folderBackupStatus.textContent = handle
    ? `Pasta: "${handle.name}"${last ? ` · último em ${new Date(last).toLocaleString('pt-BR')}` : ''} · automático: ${days ? (days === 1 ? 'todo dia' : 'toda semana') : 'desligado (⚙️)'}.`
    : 'Nenhuma pasta escolhida.';
  if (dom.backupFolderBtn) dom.backupFolderBtn.textContent = handle ? 'Gravar na pasta agora' : 'Escolher pasta de backup';
}

async function handleBackupFolderButton() {
  const handle = await getBackupFolder();
  if (!handle) { chooseBackupFolder(); return; }
  const result = await writeBackupToFolder({ interactive: true });
  showToast(result === 'ok' ? 'Backup gravado na pasta.' : result === 'partial' ? 'Backup PARCIAL gravado na pasta.' : 'Não foi possível gravar na pasta — escolha-a de novo.');
  if (result === 'denied' || result === 'error') chooseBackupFolder();
}

// @generated-exports
export { HANDLES_DB, backupFileName, buildBackupFile, canShareFiles, chooseBackupFolder, folderBackupSupported, getBackupFolder, handleBackupFolderButton, maybeAutoFolderBackup, openHandlesDb, renderFolderBackupStatus, reportBackupResult, saveBackupFolder, shareBackup, writeBackupToFolder };
// @generated-exports
